import React, { Fragment } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { resolve } from 'library/helpers/path-resolver';
import { InputEvent } from 'library/helpers/synthetic-events';

export class SelectModal extends React.Component {
  get portalElem() {
    if (!this._portal) {
      this._portal = document.createElement('div');
      this._portal.className='select-inner';
    }
    return this._portal;
  }

  static defaultProps = {
    close: ()=>{}
  }

  static propTypes = {
    parentRef: PropTypes.object,
    close: PropTypes.func,
    handleClick: PropTypes.func,
    children: PropTypes.node.isRequired,
    value: PropTypes.any.isRequired
  }

  componentDidMount() {
    document.body.appendChild(this.portalElem);
    window.addEventListener('resize', this.handleResize);
    window.addEventListener('click', this.handleClick);
    this.position();
  }

  position = () => {
    if (this.props.parentRef && this.props.parentRef.current) {
      const boundingBox = this.props.parentRef.current.getBoundingClientRect();

      this.portalElem.style.width = '';
      this.portalElem.style.height = '';

      this.portalElem.style.top = `${Math.max(16, boundingBox.top-16)}px`;
      this.portalElem.style.left = `${Math.max(16, boundingBox.left+16)}px`;
      this.portalElem.style.width = `${Math.min(window.innerWidth-32, boundingBox.width-32)}px`;

      const portalBounding = this.portalElem.getBoundingClientRect();

      this.portalElem.style.height = `${Math.min(window.innerHeight-32, portalBounding.height)}px`;
    }
  }

  handleClick = (e) => {
    if (!this.portalElem.contains(e.target)) {
      this.props.close();
    }
  }

  handleResize = () => {
    if (this._animation_request) {
      cancelAnimationFrame(this._animation_request);
    }
    this._animation_request = window.requestAnimationFrame(this.position);
  }

  componentWillUnmount() {
    document.body.removeChild(this.portalElem);
    window.removeEventListener('resize', this.handleResize);
    window.removeEventListener('click', this.handleClick);
  }

  handleSelect = (e,value) => {
    e.preventDefault();
    e.stopPropagation();
    this.props.handleClick(value);
  }

  render() {
    return ReactDOM.createPortal(
      (
        <Fragment>
          {
            React.Children.map(this.props.children, ({ props })=>(
              <div
                {...props}
                active={props.value===this.props.value ? 'true' : undefined}
                onClick={(e)=>this.handleSelect(e,props.value)}
              >
                <span>{props.children}</span>
              </div>
            ))
          }
        </Fragment>
      ),
      this.portalElem
    );
  }
}

export default class Select extends React.Component {
  state = {
    active: null,
    open: false
  }

  static propTypes = {
    onChange: PropTypes.func,
    name: PropTypes.any,
    value: PropTypes.any,
    children: PropTypes.node.isRequired,
    disabled: PropTypes.bool,
    className: PropTypes.string,
    label: PropTypes.node
  }

  static defaultProps = {
    value: '',
    name: '',
    className: '',
    label: undefined,
    onChange: ()=>{}
  }

  static getDerivedStateFromProps(nextProps) {
    const children = React.Children.toArray(nextProps.children)||[];
    for (const child in children) {
      if (children[child].type == 'fragment') {
        for(let i = 0; i < children[child].props.children.length; i++) {
          if (children[child].props.children[i] !== null)
            children.push(children[child].props.children[i]);
        }
        children.splice(child, 1);
      }
    }

    const activeChild = children.find(({props})=>props.value===nextProps.value)||resolve(children, '0', null);

    if (!activeChild) {
      return {
        active: null
      };
    }

    return {
      active: <span {...activeChild.props}>{activeChild.props.children}</span>
    };
  }

  constructor(...params) {
    super(...params);

    this.elemRef = React.createRef();
  }

  handleClick = (value) => {
    this.props.onChange(new InputEvent({
      value: value,
      name: this.props.name
    }));
    this.setState({
      open: false
    });
  }

  render() {
    const {
      open,
      active
    } = this.state;

    const {
      value,
      children,
      label,
      ...props
    } = this.props;

    return (
      <div ref={this.elemRef} onClick={(e)=>{
        e.preventDefault();
        e.stopPropagation();
        if (this.props.disabled) return;
        this.setState({open:!open});
      }} className="select" {...props}>
        { label||active }
        {
          open
            ? (
              <SelectModal
                close={()=>this.setState({open:false})}
                handleClick={this.handleClick}
                parentRef={this.elemRef}
                value={value}
              >
                {children}
              </SelectModal>
            )
            : null
        }
      </div>
    );
  }
}
